"use client";

import { useState } from "react";
import { Plus, X as XIcon, Globe } from "lucide-react";

// Chip-style multi-value input for "allowed redirect domains". Replaces
// a free-form textarea so users can add/remove entries individually and
// see exactly what's saved. Normalisation matches what the backend does
// in `normalizeDomains` — strip http(s)://, strip path, lowercase.

function normalizeDomain(input: string): string {
  return String(input || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "");
}

export default function DomainChipInput({
  value,
  onChange,
  placeholder = "e.g. acme.com",
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const cleaned = normalizeDomain(draft);
    if (!cleaned) return;
    if (value.includes(cleaned)) {
      setDraft("");
      return;
    }
    onChange([...value, cleaned]);
    setDraft("");
  };

  const remove = (domain: string) => {
    onChange(value.filter((d) => d !== domain));
  };

  return (
    <div>
      <div className="flex items-stretch gap-2">
        <div className="relative flex-1">
          <Globe className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                add();
              }
              if (e.key === "Backspace" && !draft && value.length > 0) {
                remove(value[value.length - 1]);
              }
            }}
            placeholder={placeholder}
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-gray-900/40 border border-gray-800 focus:border-[#61CE70]/50 focus:outline-none text-white text-sm placeholder-gray-600"
          />
        </div>
        <button
          type="button"
          onClick={add}
          disabled={!draft.trim()}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#61CE70]/10 hover:bg-[#61CE70]/15 text-[#61CE70] text-xs font-semibold border border-[#61CE70]/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus className="w-3.5 h-3.5" />
          Add
        </button>
      </div>

      {value.length === 0 ? (
        <p className="mt-3 text-xs text-gray-500 italic">
          No domains added yet. Type a domain above and press <kbd className="px-1 rounded bg-gray-800 text-gray-400">Enter</kbd> or click Add.
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {value.map((d) => (
            <span
              key={d}
              className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-lg bg-[#61CE70]/10 border border-[#61CE70]/25 text-[#61CE70] text-xs font-mono"
            >
              {d}
              <button
                type="button"
                onClick={() => remove(d)}
                className="p-0.5 rounded hover:bg-[#61CE70]/15 transition-colors"
                aria-label={`Remove ${d}`}
              >
                <XIcon className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
